import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Banner, Button, ButtonLink, Card, compareMoney, Dialog, Field, isZero, Money, PageHeader, Pill, type LinkComponent,
} from '@elysian/ui';
import { ZERO_ID, type Contact, type ContactInput, type ContactPerson, type DocumentSummary } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { termsText, invoiceState, iso, shortDate } from '../invoices/shared';
import { COUNTRIES, COUNTRY_GROUPS, countryCurrency } from '../shared/countries';
import { CurrencyOptions, useYourCurrencies } from '../shared/CurrencyPicker';
import { TheirPrices } from './TheirPrices';
import { ContactTransactions } from './ContactTransactions';
import { RecordReceipt, receiptToast, UseOnAccount } from './RecordReceipt';
import type { CashDocument } from '../../data/source';
import { ExportMenu } from '../shared/ExportMenu';
import { Pager, ShowSize, SortTh, useClientList } from '../shared/ListControls';
import { contactColumns } from '../shared/exportColumns';

const RouterLink = Link as unknown as LinkComponent;
type Role = 'customer' | 'supplier';
const WORDS = {
  customer: { title: 'Customers', one: 'customer', label: 'Customer', base: '/customers', owes: 'Owes you', doc: 'sales' },
  supplier: { title: 'Suppliers', one: 'supplier', label: 'Supplier', base: '/suppliers', owes: 'You owe', doc: 'purchase' },
} as const;

export function ContactList({ role }: { role: Role }) {
  const { source } = useSession();
  const toast = useToast((s) => s.show);
  const navigate = useNavigate();
  const w = WORDS[role];
  const [version, setVersion] = useState(0);
  const data = useAsync(() => source.contacts(role), [source, role, version], { keep: true });
  const [q, setQ] = useState('');
  const [onlyBalance, setOnlyBalance] = useState(false);
  const [adding, setAdding] = useState(false);
  const all = data.state === 'ready' ? data.data : [];
  const matching = useMemo(() => all
    .filter((c) => !onlyBalance || !isZero(c.balance))
    .filter((c) => !q.trim() || `${c.code ?? ''} ${c.name} ${c.email ?? ''} ${c.vat_number ?? ''}`.toLowerCase().includes(q.trim().toLowerCase())),
  [all, q, onlyBalance]);
  // #49, #50: 50, 100 or all, and sorting on the headings, over every contact.
  const list = useClientList(matching, {
    key: `elysian.${role}s`, initial: { sort: 'name', dir: 'asc' }, filterKey: `${q}|${onlyBalance}`,
    sorts: { code: (c) => c.code, name: (c) => c.name.toLowerCase(), email: (c) => c.email?.toLowerCase(), terms: (c) => c.payment_terms_days,
      balance: (c) => Number(c.balance), overdue: (c) => Number(c.overdue ?? 0) },
  });
  const rows = list.rows;
  // Balances are in each contact's own currency, so only euro ones are added up.
  const inEuro = (c: { currency?: string | null }) => !c.currency || c.currency === 'EUR';
  const total = all.filter(inEuro).reduce((t, c) => t + Number(c.balance), 0);
  const foreign = all.some((c) => !inEuro(c) && !isZero(c.balance));

  return (
    <>
      <PageHeader title={w.title}
        subtitle={data.state === 'ready' ? `${all.length} ${w.title.toLowerCase()} · ${w.owes.toLowerCase()} ${new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR' }).format(total)}${foreign ? ' plus balances in other currencies' : ''}` : undefined}
        actions={<>
          <ButtonLink as={RouterLink} variant="ghost" to={`/settings/import?what=${role}`}>Import</ButtonLink>
          <Button variant="primary" onClick={() => setAdding(true)}>New {w.one}</Button>
        </>} />
      {data.state === 'error' && <Banner>Couldn’t load {w.title.toLowerCase()}: {data.error.message}</Banner>}
      <Card>
        <div className="el-spread" style={{ padding: '14px 16px' }}>
          <label className="el-search" style={{ flex: '0 1 320px', minWidth: 'min(260px, 100%)' }}>
            <input id={`${role}-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, code, email or VAT number" aria-label={`Search ${w.title.toLowerCase()}`} />
          </label>
          <div className="el-row" style={{ gap: 14, alignItems: 'center' }}>
            <label className="el-check"><input type="checkbox" checked={onlyBalance} onChange={(e) => setOnlyBalance(e.target.checked)} /> Only with a balance</label>
            <ShowSize value={list.size} onChange={list.setSize} />
            <ExportMenu name={w.title} columns={contactColumns(role)} rows={list.all} disabled={data.state !== 'ready'} />
          </div>
        </div>
        <div className="el-table-wrap">
          <table className="el-table" aria-label={w.title}>
            <thead><tr>
              <SortTh label="Code" col="code" state={list.sortBy} onSort={list.setSortBy} />
              <SortTh label="Name" col="name" state={list.sortBy} onSort={list.setSortBy} />
              <SortTh label="Email" col="email" state={list.sortBy} onSort={list.setSortBy} />
              <SortTh label="Terms" col="terms" state={list.sortBy} onSort={list.setSortBy} firstDir="asc" />
              <SortTh label={w.owes} col="balance" state={list.sortBy} onSort={list.setSortBy} numeric />
              <SortTh label="Overdue" col="overdue" state={list.sortBy} onSort={list.setSortBy} numeric />
            </tr></thead>
            <tbody>
              {data.state === 'loading' && <tr><td colSpan={6}><div className="el-skeleton" style={{ height: 18 }} /></td></tr>}
              {rows.map((c) => (
                <tr key={c.id} data-href={`${w.base}/${c.id}`} onClick={(e) => (e.target as HTMLElement).closest('a') || navigate(`${w.base}/${c.id}`)}>
                  <td className="el-code">{c.code ?? <span className="el-muted">—</span>}</td>
                  <td><Link className="el-row-link" style={{ whiteSpace: 'normal' }} to={`${w.base}/${c.id}`}>{c.name}</Link>{c.vat_number && <div className="el-hint">VAT {c.vat_number}</div>}</td>
                  <td className="el-hint">{c.email ?? '—'}</td>
                  <td>{termsText(c)}</td>
                  <td className="el-num">{isZero(c.balance) ? '—' : <Money value={c.balance} currency={c.currency ?? 'EUR'} />}</td>
                  <td className="el-num">{c.overdue && compareMoney(c.overdue, '0') > 0 ? <Money value={c.overdue} currency={c.currency ?? 'EUR'} tone="warn" /> : '—'}</td>
                </tr>
              ))}
              {data.state === 'ready' && rows.length === 0 && <tr><td colSpan={6} className="el-muted">No {w.title.toLowerCase()} match.</td></tr>}
            </tbody>
          </table>
        </div>
        {data.state === 'ready' && <Pager count={list.count} page={list.page} size={list.size} shown={rows.length} onPage={list.setPage} />}
      </Card>
      {adding && <ContactForm role={role} onClose={() => setAdding(false)} onSaved={(c) => { setAdding(false); toast(`${c.name} added`); setVersion((v) => v + 1); navigate(`${w.base}/${c.id}`); }} />}
    </>
  );
}

export function ContactPage({ role }: { role: Role }) {
  const { id = '' } = useParams();
  const { source, today } = useSession();
  const toast = useToast((s) => s.show);
  const w = WORDS[role];
  const now = iso(today());
  const [version, setVersion] = useState(0);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [receiving, setReceiving] = useState(false);
  const [using, setUsing] = useState<CashDocument | null>(null);
  const data = useAsync(async () => {
    const [contact, docs, held] = await Promise.all([source.contact(id), source.documents({ contactId: id }),
      source.onAccount(id)]);
    return { contact, docs: docs.filter((d) => d.type.startsWith(w.doc)), held };
  }, [source, id, version], { keep: true });

  if (data.state === 'loading') return <PageHeader title={w.title} subtitle="Loading…" />;
  if (data.state === 'error') return <><PageHeader title={w.title} /><Banner action={<Link to={w.base}>All {w.title.toLowerCase()}</Link>}>Couldn’t open this {w.one}: {data.error.message}</Banner></>;
  const { contact: c, docs, held } = data.data;
  const open = docs.filter((d) => (d.status === 'posted' || d.status === 'part_paid') && !isZero(d.outstanding));
  const address = c.address ? [c.address.line1, c.address.line2, c.address.town, c.address.county, c.address.postcode].filter(Boolean).join(', ') : '';
  const docLink = (d: DocumentSummary) => (d.type === 'sales_invoice' ? `/invoices/${d.id}` : d.type === 'sales_credit' ? `/credit-notes/${d.id}` : `/bills/${d.id}`);

  async function statement() {
    setBusy(true);
    try {
      const r = await source.createStatements({ contact_ids: [c.id], as_at: now, send: true, type: 'open_items', only_overdue: false });
      toast(r.emailed ? `Statement emailed to ${c.email}` : r.no_email?.length ? `${c.name} has no email address; the statement is ready to download` : 'Nothing to put on a statement');
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div style={{ marginBottom: 6 }}><Link to={w.base} className="el-hint">← All {w.title.toLowerCase()}</Link></div>
      <PageHeader title={c.name}
        subtitle={<>{/* #34: what's been paid to them (or received from them) this financial year, before what's owed. */}
          {role === 'supplier' ? 'Paid this year' : 'Received this year'}: <Money value={(role === 'supplier' ? c.paid_this_year : c.received_this_year) ?? '0'} currency={c.currency ?? 'EUR'} />
          {' · '}{w.owes}: <Money value={c.balance} currency={c.currency ?? 'EUR'} />{c.overdue && compareMoney(c.overdue, '0') > 0 && <> · <Money value={c.overdue} currency={c.currency ?? 'EUR'} tone="warn" /> overdue</>}</>}
        actions={
          <>
            <Button onClick={() => setEditing(true)}>Edit details</Button>
            <Button onClick={() => setReceiving(true)}>{role === 'customer' ? 'Record a receipt' : 'Record a payment'}</Button>
            {role === 'customer' && <Button disabled={busy || isZero(c.balance)} onClick={() => void statement()}>Email a statement</Button>}
            {role === 'customer' && <ButtonLink as={RouterLink} variant="primary" to={`/invoices/new?customer=${c.id}`}>New invoice</ButtonLink>}
            {role === 'supplier' && <ButtonLink as={RouterLink} variant="primary" to={`/bills/new?supplier=${c.id}`}>New bill</ButtonLink>}
          </>
        } />
      <div className="el-two-col" data-wide="right">
        <Card as="section" padding="18px 20px" aria-labelledby="c-details">
          <h2 id="c-details" className="el-h2" style={{ marginBottom: 12 }}>Details</h2>
          <dl className="el-kv">
            <dt>Code</dt><dd>{c.code ?? '—'}</dd>
            <dt>Email</dt><dd>{c.email ?? '—'}</dd>
            <dt>VAT number</dt><dd>{c.vat_number ?? '—'}</dd>
            <dt>Pays in</dt><dd>{termsText(c)}</dd>
            {role === 'customer' && c.invoice_template_id && <><dt>Invoice layout</dt><dd><LayoutName id={c.invoice_template_id} /></dd></>}
            <dt>Country</dt><dd>{c.country ?? 'IE'}</dd>
            <dt>Currency</dt><dd>{c.currency ?? 'EUR'}</dd>
            <dt>Address</dt><dd>{address || '—'}</dd>
            {c.is_customer && c.is_supplier && <><dt>Also</dt><dd>{role === 'customer' ? 'A supplier' : 'A customer'}</dd></>}
          </dl>
          {(c.people?.length ?? 0) > 0 && (
            <>
              <h3 className="el-kv-head">Contact people</h3>
              <ul className="el-people-list" aria-label="Contact people">
                {c.people!.map((p, i) => (
                  <li key={i}>
                    <span><strong>{p.name}</strong>{p.title && <span className="el-muted"> · {p.title}</span>}</span>
                    <span className="el-hint">
                      {p.phone && <a href={`tel:${p.phone.replace(/\s/g, '')}`}>{p.phone}</a>}
                      {p.email && <a href={`mailto:${p.email}`}>{p.email}</a>}
                      {role === 'customer' && (p.gets_invoices || p.gets_statements) && <span>Gets {[p.gets_invoices && 'invoices', p.gets_statements && 'statements'].filter(Boolean).join(' and ')}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
          {c.notes && (
            <>
              <h3 className="el-kv-head">Notes</h3>
              <p className="el-contact-notes-text">{c.notes}</p>
            </>
          )}
        </Card>
        <Card as="section" padding="18px 0 8px" aria-labelledby="c-docs">
          <div className="el-card-head"><h2 id="c-docs" className="el-h2">{role === 'customer' ? 'Invoices and credit notes' : 'Bills'}</h2><span className="el-hint">{open.length} open</span></div>
          <div className="el-table-wrap">
            <table className="el-table" aria-label="Documents">
              <tbody>
                {docs.map((d) => {
                  const st = d.type.endsWith('credit')
                    ? { tone: (isZero(d.outstanding) ? 'ok' : 'info') as 'ok' | 'info', label: isZero(d.outstanding) ? 'Used' : 'Credit to use' }
                    : invoiceState(d, now);
                  return (
                    <tr key={d.id}>
                      <td><Link className="el-row-link" to={docLink(d)}>{d.doc_no ?? (d.supplier_ref ? d.supplier_ref : 'Draft')}</Link>{d.type === 'purchase_bill' && d.supplier_ref && d.doc_no && <div className="el-hint">{d.supplier_ref}</div>}</td>
                      <td>{shortDate(d.issue_date)}</td>
                      <td><Pill tone={st.tone}>{st.label}</Pill></td>
                      <td className="el-num"><Money value={d.type.endsWith('credit') ? `-${d.gross_total.replace('-', '')}` : d.gross_total} currency={d.currency} /></td>
                    </tr>
                  );
                })}
                {docs.length === 0 && <tr><td className="el-muted">Nothing yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
        {held.length > 0 && (<div style={{ gridColumn: '1 / -1', minWidth: 0 }}>
          <Card as="section" padding="18px 0 8px" aria-labelledby="c-held">
            <div className="el-card-head"><h2 id="c-held" className="el-h2">Credit on account</h2>
              <span className="el-hint">{role === 'customer' ? 'Money received but not yet used against invoices' : 'Money paid but not yet used against bills'}</span></div>
            <div className="el-table-wrap">
              <table className="el-table" aria-label="Credit on account">
                <tbody>
                  {held.map((r) => (
                    <tr key={r.id}>
                      <td><Link className="el-row-link" to={`/receipts/${r.id}`}>{r.number}</Link><div className="el-hint">{shortDate(r.date)} · {r.bank_account?.name}{r.reference ? ` · ${r.reference}` : ''}</div></td>
                      <td className="el-num"><Money value={r.on_account} /> <span className="el-hint">of <Money value={r.amount} /></span></td>
                      <td className="el-num" style={{ whiteSpace: 'nowrap' }}><Button size="sm" onClick={() => setUsing(r)}>{role === 'customer' ? 'Use against invoices' : 'Use against bills'}</Button>{' '}
                        <Link className="el-hint" to={`/receipts/${r.id}`}>Refund or change</Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>)}
        <div style={{ gridColumn: '1 / -1', minWidth: 0 }}><ContactTransactions key={`${c.id}:${version}`} contactId={c.id} role={role} /></div>
        {role === 'customer' && <div style={{ gridColumn: '1 / -1', minWidth: 0 }}><TheirPrices contactId={c.id} name={c.name} /></div>}
      </div>
      {receiving && <RecordReceipt role={role} contactId={c.id} contactName={c.name} onClose={() => setReceiving(false)}
        onDone={(r) => { setReceiving(false); toast(receiptToast(r)); setVersion((v) => v + 1); }} />}
      {using && <UseOnAccount receipt={using} onClose={() => setUsing(null)}
        onDone={(msg) => { setUsing(null); toast(msg); setVersion((v) => v + 1); }} />}
      {editing && <ContactForm role={role} contact={c} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); toast('Details saved'); setVersion((v) => v + 1); }} />}
    </>
  );
}

/** The Address box's lines as the address record keeps them: the first is line 1, the rest (joined) line 2. */
export function streetLines(text: string): { line1?: string; line2?: string } {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  return { line1: lines[0] || undefined, line2: lines.slice(1).join(', ') || undefined };
}

function ContactForm({ role, contact, onClose, onSaved }: { role: Role; contact?: Contact; onClose: () => void; onSaved: (c: Contact) => void }) {
  const { source } = useSession();
  // How codes are given (#20): automatic codes fill in when left blank.
  const codeSettings = useAsync(() => source.organisationSettings().then((o) => o.contact_codes), [source]);
  // #43: the customer's own invoice layout, from the named ones beside the default.
  const layouts = useAsync(() => source.templates().then((ts) => ts.filter((t) => t.kind === 'sales_invoice')), [source]);
  const [layoutId, setLayoutId] = useState(contact?.invoice_template_id ?? '');
  const auto = codeSettings.state === 'ready' && codeSettings.data?.mode === 'auto';
  const [f, setF] = useState({
    code: contact?.code ?? '', name: contact?.name ?? '', email: contact?.email ?? '', vat: contact?.vat_number ?? '', terms: String(contact?.payment_terms_days ?? 30), basis: contact?.payment_terms_basis ?? 'invoice_date',
    // #29: one Address box; its first line is line 1 and the rest line 2 (so an imported line 2 is kept).
    street: [contact?.address?.line1, contact?.address?.line2].filter(Boolean).join('\n'), town: contact?.address?.town ?? '', county: contact?.address?.county ?? '', postcode: contact?.address?.postcode ?? '',
    country: contact?.country ?? 'IE', currency: contact?.currency ?? 'EUR',
    notes: contact?.notes ?? '',
  });
  // #30: the people at the customer or supplier.
  const [people, setPeople] = useState<ContactPerson[]>(contact?.people ?? []);
  const setPerson = (i: number, patch: Partial<ContactPerson>) => setPeople((list) => list.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof f, v: string) => setF((x) => ({ ...x, [k]: v }));
  const [currencyTouched, setCurrencyTouched] = useState(Boolean(contact));
  // A new contact's currency follows its country until it's chosen by hand.
  const setCountry = (code: string) => setF((x) => ({ ...x, country: code, ...(currencyTouched ? {} : { currency: countryCurrency(code) ?? x.currency }) }));
  const { base, yours } = useYourCurrencies();
  const terms = Number(f.terms);
  const codeError = f.code.trim() && !/^[A-Z0-9][A-Z0-9._/&-]{0,19}$/.test(f.code.trim()) ? 'Letters and digits (. _ / & - inside), no spaces' : null;
  const termsOk = Number.isInteger(terms) && terms >= 0 && terms <= 365;

  async function save() {
    setBusy(true);
    setError(null);
    const input: ContactInput = {
      name: f.name.trim(), email: f.email.trim() || undefined,
      // Blank on a new contact: none, or the next automatic code. Cleared on an existing one: removed.
      ...(f.code.trim() ? { code: f.code.trim().toUpperCase() } : contact?.code ? { code: '' } : {}), vat_number: f.vat.replace(/\s/g, '').toUpperCase() || undefined,
      payment_terms_days: terms, payment_terms_basis: f.basis as 'invoice_date' | 'end_of_month', country: f.country.toUpperCase(), currency: f.currency,
      address: { ...streetLines(f.street), town: f.town.trim() || undefined, county: f.county.trim() || undefined, postcode: f.postcode.trim() || undefined },
      ...(contact ? {} : { is_customer: role === 'customer', is_supplier: role === 'supplier' }),
      people, notes: f.notes,
      // #43: a chosen layout, or back to the default (the zero id) when one is cleared.
      ...(role === 'customer' ? { invoice_template_id: layoutId || (contact?.invoice_template_id ? ZERO_ID : undefined) } : {}),
    };
    try {
      onSaved(contact ? await source.updateContact(contact.id, input) : await source.createContact(input));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Dialog wide title={`${contact ? 'Edit' : 'New'} ${WORDS[role].label}`} onClose={onClose}
      actions={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" disabled={busy || !f.name.trim() || !termsOk || Boolean(codeError)} onClick={() => void save()}>Save</Button></>}>
      {/* #29: laid out as Brian's mock-up, in two columns (one on a phone). */}
      <div className="el-contact-form">
        <Field id="ct-name" label="Company / Name"><input id="ct-name" className="el-input" value={f.name} onChange={(e) => set('name', e.target.value)} /></Field>
        <Field id="ct-code" label="Code" error={codeError}
          hint={contact ? undefined : auto ? 'Left blank, the next code is given' : 'Optional, such as SWORDS01'}>
          <input id="ct-code" className="el-input el-code" autoComplete="off" spellCheck={false} maxLength={20} value={f.code}
            onChange={(e) => set('code', e.target.value.toUpperCase())} placeholder={!contact && auto ? 'Automatic' : undefined} />
        </Field>

        <Field id="ct-street" label="Address">
          <textarea id="ct-street" className="el-input el-address" rows={3} value={f.street} onChange={(e) => set('street', e.target.value)} />
        </Field>
        <div aria-hidden="true" />

        <Field id="ct-town" label="Town / City"><input id="ct-town" className="el-input" value={f.town} onChange={(e) => set('town', e.target.value)} /></Field>
        <Field id="ct-post" label="Eircode / Post Code"><input id="ct-post" className="el-input" value={f.postcode} onChange={(e) => set('postcode', e.target.value)} /></Field>

        <Field id="ct-county" label="County / Area"><input id="ct-county" className="el-input" value={f.county} onChange={(e) => set('county', e.target.value)} /></Field>
        <Field id="ct-country" label="Country">
          <select id="ct-country" className="el-input" value={f.country} onChange={(e) => setCountry(e.target.value)}>
            {!COUNTRIES.some((c) => c.code === f.country) && <option value={f.country}>{f.country}</option>}
            {COUNTRY_GROUPS.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </optgroup>
            ))}
          </select>
        </Field>

        {/* #31: the email, easier to read in the wide column; the VAT number beside Payment Terms. */}
        <Field id="ct-email" label="Email" hint={role === 'customer' ? 'Invoices and statements go here when no one below is ticked' : undefined}>
          <input id="ct-email" type="email" className="el-input" value={f.email} onChange={(e) => set('email', e.target.value)} />
        </Field>
        <Field id="ct-currency" label="Default Currency" hint="New documents start in this currency">
          <select id="ct-currency" className="el-input" value={f.currency} onChange={(e) => { setCurrencyTouched(true); set('currency', e.target.value); }}>
            <CurrencyOptions base={base} yours={yours} />
          </select>
        </Field>

        <div className="el-terms-field">
          <Field id="ct-terms" label="Payment Terms" hint={role === 'customer' ? 'Days they have to pay' : 'Days we have to pay'} error={termsOk ? null : '0 to 365'}>
            <input id="ct-terms" className="el-input el-num el-terms" inputMode="numeric" value={f.terms} onChange={(e) => set('terms', e.target.value)} />
          </Field>
          {/* #32: the days count from the invoice date (the default) or from the end of its month. */}
          <fieldset className="el-radio-row">
            <legend className="el-vh">Payment terms count from</legend>
            <label className="el-check"><input type="radio" name="ct-basis" value="invoice_date" checked={f.basis !== 'end_of_month'} onChange={() => set('basis', 'invoice_date')} /> Date of invoice</label>
            <label className="el-check"><input type="radio" name="ct-basis" value="end_of_month" checked={f.basis === 'end_of_month'} onChange={() => set('basis', 'end_of_month')} /> End of month</label>
          </fieldset>
        </div>
        <Field id="ct-vat" label="VAT Number" hint="Needed for reverse charge and EU sales"><input id="ct-vat" className="el-input" value={f.vat} onChange={(e) => set('vat', e.target.value)} /></Field>

        {role === 'customer' && (layouts.state !== 'ready' || layouts.data.some((t) => !t.is_default)) && (
          <Field id="ct-layout" label="Invoice layout" hint="Their invoices and credit notes use it. Add layouts under Settings → Invoice design">
            <select id="ct-layout" className="el-input" value={layoutId} onChange={(e) => setLayoutId(e.target.value)}>
              <option value="">{layouts.state === 'ready' ? `${layouts.data.find((t) => t.is_default)?.name ?? 'Standard'} (the default)` : 'The default'}</option>
              {layouts.state === 'ready' && layouts.data.filter((t) => !t.is_default).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </Field>
        )}
      </div>

      <Field id="ct-notes" label="Notes">
        <textarea id="ct-notes" className="el-input el-contact-notes" rows={3} maxLength={4000} value={f.notes} onChange={(e) => set('notes', e.target.value)} />
      </Field>

      <section className="el-people" aria-labelledby="ct-people">
        <div className="el-people-head">
          <h3 id="ct-people">Contact people</h3>
          <span className="el-hint">{role === 'customer' ? 'Tick who gets emailed invoices and statements' : 'The people you deal with there'}</span>
        </div>
        {people.map((p, i) => {
          const n = i + 1;
          return (
            <div key={i} className="el-person" role="group" aria-label={`Contact person ${n}`}>
              <div className="el-person-fields">
                <Field id={`cp-name-${i}`} label="Name"><input id={`cp-name-${i}`} className="el-input" value={p.name ?? ''} onChange={(e) => setPerson(i, { name: e.target.value })} /></Field>
                <Field id={`cp-title-${i}`} label="Job title"><input id={`cp-title-${i}`} className="el-input" value={p.title ?? ''} onChange={(e) => setPerson(i, { title: e.target.value })} /></Field>
                <Field id={`cp-phone-${i}`} label="Phone"><input id={`cp-phone-${i}`} type="tel" className="el-input" value={p.phone ?? ''} onChange={(e) => setPerson(i, { phone: e.target.value })} /></Field>
                <Field id={`cp-email-${i}`} label="Email"><input id={`cp-email-${i}`} type="email" className="el-input" value={p.email ?? ''} onChange={(e) => setPerson(i, { email: e.target.value })} /></Field>
              </div>
              <div className="el-person-foot">
                {/* #33: invoices and statements only go to customers, so suppliers don't show the ticks (any set on the customer side are kept). */}
                {role === 'customer' && <>
                  <label className="el-check"><input type="checkbox" checked={!!p.gets_invoices} onChange={(e) => setPerson(i, { gets_invoices: e.target.checked })} /> Gets invoices</label>
                  <label className="el-check"><input type="checkbox" checked={!!p.gets_statements} onChange={(e) => setPerson(i, { gets_statements: e.target.checked })} /> Gets statements</label>
                </>}
                <Button size="sm" variant="ghost" onClick={() => setPeople((list) => list.filter((_, j) => j !== i))} aria-label={`Remove contact person ${n}`}>Remove</Button>
              </div>
            </div>
          );
        })}
        <div><Button size="sm" onClick={() => setPeople((list) => [...list, { name: '', gets_invoices: false, gets_statements: false }])}>Add a contact person</Button></div>
      </section>
      {error && <div className="el-error-box" role="alert">{error}</div>}
    </Dialog>
  );
}

/** #43: a customer's invoice layout by name. */
function LayoutName({ id }: { id: string }) {
  const { source } = useSession();
  const t = useAsync(() => source.templates().then((ts) => ts.find((x) => x.id === id)), [source, id]);
  return <>{t.state === 'ready' ? t.data?.name ?? 'The default' : '…'}</>;
}
